<!-- Standard form checkbox with a tick box, label content, and validation error display. -->

<script lang="ts" setup>
import { useCheckbox } from '@nzyme/vue-forms/components/useCheckbox.jsx';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { computed } from 'vue';
import type { PropType } from 'vue';

import CheckboxTick from './CheckboxTick.vue';
import FieldInfo from './FieldInfo.vue';

interface CheckboxSlots {
  default: { checked: boolean };
}

const props = defineProps({
  ...useCheckbox.props,
  contentClass: classProp,
  errors: Array as PropType<string[]>,
});

defineSlots<Slots<CheckboxSlots>>();
defineEmits(useCheckbox.emits);

const { Checkbox, field } = useCheckbox();

const errorsToShow = computed(() => {
  let errors = field.errors;

  if (props.errors) {
    errors = errors.concat(props.errors);
  }

  return errors.filter(Boolean);
});

const hasError = computed(() => errorsToShow.value.length > 0);
</script>

<template>
  <label
    :class="[css.wrapper, disabled && css.disabled, readonly && css.readonly]"
    :aria-disabled="disabled || undefined"
  >
    <div :class="css.checkbox">
      <Checkbox :class="[css.tick, readonly && css.readonly]">
        <CheckboxTick
          :checked="!!field.value"
          :readonly="readonly"
          :has-error="hasError"
          :disabled="disabled"
        />
      </Checkbox>

      <div :class="[css.content, contentClass]">
        <slot :checked="!!field.value" />
      </div>
    </div>

    <FieldInfo
      v-for="(error, index) in errorsToShow"
      :key="index"
      type="error"
    >
      {{ error }}
    </FieldInfo>
  </label>
</template>

<style lang="scss" module="css">
.wrapper {
  display: flex;
  flex-direction: column;
  user-select: none;
  cursor: pointer;

  &.readonly {
    cursor: default;
  }

  &.disabled {
    cursor: default;
  }
}

.checkbox {
  display: grid;
  grid-template-columns: auto 1fr;
  font-size: $font-size-14;
  align-items: flex-start;
}

.tick {
  &:not(.readonly):hover > * {
    border-color: $color-gray-700;
    background-color: $color-gray-50;
  }

  &:not(.readonly):focus-visible {
    @include input-focus-outline;
  }
}

.content {
  align-self: center;

  .disabled & {
    filter: grayscale(1);
    color: $color-gray-600;
    opacity: 0.5;
  }
}
</style>
